import * as React from "react";
import { connect } from "react-redux";
import { Grid, Header, Button, Loader } from "semantic-ui-react";
import axios from "axios";

import { isRequestActive } from "redux-request-loading";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import {
    questionnaireTypes,
    QuestionConfig,
    MasterQuestionConfig,
    CustomQuestionConfig,
    QuestionnaireConfig,
    QuestionType,
    ConfigurableQuestionnaireKey
} from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { LOAD_QUESTIONNAIRE_CONFIG, SAVE_QUESTIONNAIRE_CONFIG } from "@teamprevent/common/questionnaires/questionnaireConfig/actionTypes";
import { TermsAndConditionsOfUseConfig } from "@teamprevent/common/termsAndConditions/model";
import { toast } from "@teamprevent/common/toasts";

import { loadQuestionnaireConfig, saveQuestionnaireConfig } from "../actions";
import { ApplicationState } from "../../applicationState";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { QuestionnaireConfigApi } from "../QuestionnaireConfigApi";

import { QuestionEdit } from "./QuestionEdit";
import { AdditionalQuestionsEdit } from "./AdditionalQuestionsEdit";
import { AdditionalCustomQuestionEdit } from "./AdditionalCustomQuestionEdit";

import { Link, push } from "redux-little-router";

interface StateProps {
    masterQuestions: MasterQuestionConfig[];
    questionnaireConfig: QuestionnaireConfig;
    questionnaireName: string;
    questionnaireType: ConfigurableQuestionnaireKey;
    basePath: string;
    loading: boolean;
    saving: boolean;
    customerId: string;
}

interface DispatchProps {
    save: (customerId: string, questionnaireType: string, questionnaireConfig: QuestionnaireConfig) => Promise<void>;
    dispatchPush: (href: string) => void;
    loadQuestionnaireConfig: (customerId: string, questionnaireType: string) => void;
}

interface Props {
    save: (questionnaireConfig: QuestionnaireConfig) => Promise<void>;
    goBack: () => void;
    masterQuestions: MasterQuestionConfig[];
    questionnaireConfig: QuestionnaireConfig;
    questionnaireName: string;
    basePath: string;
    loading: boolean;
    saving: boolean;
    questionnaireType: ConfigurableQuestionnaireKey;
    customerId: string;
    loadQuestionnaireConfig: () => void;
}

interface State {
    customQuestions: QuestionConfig[];
    additionalQuestions: CustomQuestionConfig[];
    loadingTermsAndConditionsOfUseDefaultText: boolean;
    termsAndConditionsOfUseDefaultText: string;
}

class QuestionnaireEditUnconnected extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            customQuestions: this.props.questionnaireConfig.questions,
            additionalQuestions: this.props.questionnaireConfig.additionalQuestions,
            loadingTermsAndConditionsOfUseDefaultText: true,
            termsAndConditionsOfUseDefaultText: null
        };
        this.onQuestionChanged = this.onQuestionChanged.bind(this);
        this.onAdditionalQuestionChanged = this.onAdditionalQuestionChanged.bind(this);
        this.save = this.save.bind(this);
        this.renderQuestionEdit = this.renderQuestionEdit.bind(this);
    }

    public componentDidMount() {
        this.props.loadQuestionnaireConfig();

        if (this.props.questionnaireType === TermsAndConditionsOfUseConfig) {
            const questionnaireConfigApi = new QuestionnaireConfigApi(axios);
            questionnaireConfigApi.getTermsAndConditionsOfUseDefaultText()
                .then(configText =>
                    this.setState({ loadingTermsAndConditionsOfUseDefaultText: false, termsAndConditionsOfUseDefaultText: configText.questionText })
                );
        } else {
            this.setState({ loadingTermsAndConditionsOfUseDefaultText: false });
        }
    }

    public componentDidUpdate(oldProps: Props) {
        if (oldProps.questionnaireConfig !== this.props.questionnaireConfig) {
            this.setState({
                customQuestions: this.props.questionnaireConfig.questions,
                additionalQuestions: this.props.questionnaireConfig.additionalQuestions
            });
        }
    }

    public renderQuestionEdit() {
        return (
            this.props.masterQuestions.map(masterQuestion =>
                (<QuestionEdit
                    masterQuestion={masterQuestion}
                    customQuestion={this.state.customQuestions.find(q => q.id === masterQuestion.id)}
                    onChanged={this.onQuestionChanged}
                    key={masterQuestion.id}
                    questionnaireType={this.props.questionnaireType}
                    customQuestionText={this.state.termsAndConditionsOfUseDefaultText}
                />)
            )
        );
    }

    public render() {
        const allowedMandatoryPropertyTypes: ConfigurableQuestionnaireKey[] = ["referral", "ppqManagerSection", "physiotherapyMgrReferral", "genericCase"];
        return (
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column>
                        <Header as="h1" content={`${this.props.questionnaireName} Customisation`} />
                    </Grid.Column>
                </Grid.Row>
                { allowedMandatoryPropertyTypes.includes(this.props.questionnaireType) &&
                    <Grid.Row>
                        <Loader active={this.props.loading} />
                        {!this.props.loading &&
                            this.state.customQuestions &&
                            this.props.masterQuestions &&
                            <AdditionalCustomQuestionEdit
                                questions={this.state.customQuestions}
                                onChanged={this.onQuestionChanged}
                                masterQuestion={this.props.masterQuestions}
                            />
                        }
                    </Grid.Row>
                }
                <Grid.Row>
                    <Loader active={this.props.loading || this.state.loadingTermsAndConditionsOfUseDefaultText} />
                    {!this.props.loading &&
                        !this.state.loadingTermsAndConditionsOfUseDefaultText &&
                        this.state.customQuestions &&
                        this.renderQuestionEdit()
                    }
                </Grid.Row>
                {!this.props.loading &&
                    !this.state.loadingTermsAndConditionsOfUseDefaultText &&
                    this.state.additionalQuestions && this.state.additionalQuestions.length ?
                    <>
                        <Grid.Row>
                            <Grid.Column>
                                <Header as="h2" content="Further questions" />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <AdditionalQuestionsEdit
                                customerId={this.props.customerId}
                                questionnaireType={this.props.questionnaireType}
                                questions={this.state.additionalQuestions}
                                onChanged={this.onAdditionalQuestionChanged}
                            />
                        </Grid.Row>
                    </>
                    : null
                }
                <Grid.Row>
                    <Grid.Column>
                        {!this.props.loading &&
                            <Button
                                content="Save"
                                floated="right"
                                onClick={() => this.save(allowedMandatoryPropertyTypes)}
                                loading={this.props.saving}
                            />
                        }
                        <Button content="Cancel" as={Link} href={this.props.basePath} floated="right" />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    }

    private onQuestionChanged(questionConfig: QuestionConfig) {
        this.setState(prevState => (
            {
                customQuestions: [
                    ...prevState.customQuestions.filter(q => q.id !== questionConfig.id),
                    questionConfig
                ]
            }
        ));
    }

    private onAdditionalQuestionChanged(questionConfig: CustomQuestionConfig) {
        this.setState(prevState => {
            const indexOfQuestion = prevState.additionalQuestions.findIndex(q => q.id === questionConfig.id);
            return {
                additionalQuestions: [
                    ...prevState.additionalQuestions.slice(0, indexOfQuestion),
                    questionConfig,
                    ...prevState.additionalQuestions.slice(indexOfQuestion + 1)
                ]
            };
        });
    }

    private async save(validateMandatoryProperties: string[] = []) {
        const hasAdditionalQuestions = this.state.additionalQuestions?.length > 0;
        const isSomeAdditionalQuestionWithEmptyText = this.state.additionalQuestions?.some(q => !q.questionText);
        if (hasAdditionalQuestions && isSomeAdditionalQuestionWithEmptyText) {
            toast.warning("Question text can not be empty.");
            return;
        }

        const additionalDropdownQuestions = this.state.additionalQuestions?.filter(q => q.questionType === QuestionType.DropdownQuestion) ?? [];
        if (additionalDropdownQuestions.length > 0) {
            if (additionalDropdownQuestions.some(q => q.options.length === 0 || (q.options.length === 1 && !q.options[0]))) {
                toast.warning("Please provide dropdown options.");
                return;
            }
        }

        if (validateMandatoryProperties.includes(this.props.questionnaireType)) {
            const employeeIdQuestion = this.state.customQuestions.find(q => q.id === "EmployeeId");
            const dobIdQuestion = this.state.customQuestions.find(q => q.id === "ReferredDob");

            const employeeIdQuestionConfig = (employeeIdQuestion as CustomQuestionConfig);
            if (!employeeIdQuestionConfig?.isMandatory && !(dobIdQuestion as CustomQuestionConfig)?.isMandatory)
            {
                toast.warning("One of 'DOB' or 'Employee Id' has to be mandatory to submit");
                return;
            }

            if (employeeIdQuestionConfig?.disabled && employeeIdQuestionConfig?.isMandatory)
            {
                toast.warning("Employee Id cannot be disabled and mandatory");
                return;
            }
        }

        await this.props.save({
            questions: this.state.customQuestions,
            additionalQuestions: this.state.additionalQuestions
        });
        this.props.goBack();
    }
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const questionnaireType = state.router.params.questionnaireType as ConfigurableQuestionnaireKey;
    const questionnaireConfig = state.questionnaireConfig[questionnaireType] || { questions: null, additionalQuestions: null };
    return {
        masterQuestions: questionnaireTypes[questionnaireType].masterConfig,
        questionnaireConfig,
        questionnaireName: questionnaireTypes[questionnaireType].displayName,
        questionnaireType,
        customerId: customerIdSelector(state),
        basePath: state.router.pathname.replace("/edit", ""),
        loading: isRequestActive(state, LOAD_QUESTIONNAIRE_CONFIG),
        saving: isRequestActive(state, SAVE_QUESTIONNAIRE_CONFIG)
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => ({
    save: (customerId: string, questionnaireType: string, questionnaireConfig: QuestionnaireConfig) =>
        dispatch(saveQuestionnaireConfig(customerId, questionnaireType, questionnaireConfig)),
    dispatchPush: href => dispatch(push(href)),
    loadQuestionnaireConfig: (customerId: string, questionnaireType: string) => {
        dispatch(loadQuestionnaireConfig({ customerId, questionnaireType }));
    }
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps): Props => ({
    ...stateProps,
    save: (questionnaireConfig: QuestionnaireConfig) => dispatchProps.save(stateProps.customerId, stateProps.questionnaireType, questionnaireConfig),
    goBack: () => dispatchProps.dispatchPush(stateProps.basePath),
    loadQuestionnaireConfig: () => dispatchProps.loadQuestionnaireConfig(stateProps.customerId, stateProps.questionnaireType)
});

export const QuestionnaireEdit = connect(mapStateToProps, mapDispatchToProps, mergeProps)(QuestionnaireEditUnconnected);
